import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';

import {
  Card,
  CardCloseButton,
  CardOptions,
  DateField,
  ErrorMessage,
  Form,
  SubmitButton,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import formatDate from '@/utils/formatter/formatDate';
import {
  ChangePlayerBirthDateMutation,
  ChangePlayerBirthDateMutationVariables,
  PlayerBirthDateFormQuery,
} from './__generated__/PlayerBirthDateForm';

const query = graphql`
  query SanityPlayerBirthDateForm {
    sanityPlayerBirthDateForm {
      title {
        ...SanityLocaleString
      }
      birthDate {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const playerQuery = gql`
  query PlayerBirthDateForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      birthDate
    }
  }
`;

const playerMutation = gql`
  mutation ChangePlayerBirthDate($playerId: ID!, $birthDate: LocalDate!) {
    changePersonalDetails(playerId: $playerId, birthDate: $birthDate) {
      id
      birthDate
    }
  }
`;

const PlayerBirthDateForm: FC<{ playerId: string }> = ({ playerId }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerBirthDateFormQuery>(query);

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();

  const form = staticData.sanityPlayerBirthDateForm;

  const today = useMemo(() => new Date(), []);

  const [{ data }] = useQuery<PlayerBirthDateFormQuery>({
    query: playerQuery,
    variables: { playerId },
  });

  const [updatePlayerState, updatePlayer] = useMutation<
    ChangePlayerBirthDateMutation,
    ChangePlayerBirthDateMutationVariables
  >(playerMutation);

  const defaultValues = {
    birthDate: data?.player?.birthDate ? new Date(data.player.birthDate) : null,
  };

  const { close } = useModalContext();

  const { t } = useTranslate();

  const onSubmit = (values: typeof defaultValues) => {
    if (!data?.player?.id || !values.birthDate) {
      return;
    }

    updatePlayer({
      playerId: data?.player.id,
      birthDate: formatDate(values.birthDate, true),
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form || !data) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <Form
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          className="grid grid-cols-2 sm:grid-cols-3 gap-6"
        >
          <DateField
            className="col-span-full"
            title={t(form.birthDate)}
            maxDate={today}
            name="birthDate"
            id="PlayerBirthDateForm__birthDate"
            showMonthDropdown
            showYearDropdown
          />

          <ErrorMessage message={errorMessage} />
          <SubmitButton
            value={t(form.submit)}
            disabled={updatePlayerState.fetching}
          />
        </Form>
      </div>
    </Card>
  );
};

export default PlayerBirthDateForm;
